import { useEffect, useMemo, useState } from "react";
import {
  addAppointmentPayment,
  addAppointmentResource,
  createAppointment,
  getAppointment,
  getAppointments,
  getAppointmentsConfig,
  removeAppointmentResource,
  updateAppointment,
} from "../api/appointmentsApi";
import { useAuth } from "../../../auth/AuthContext";

import AppointmentFilters from "../components/AppointmentFilters";
import AppointmentList from "../components/AppointmentList";
import AppointmentCalendar from "../components/AppointmentCalendar";
import AppointmentFormModal from "../components/AppointmentFormModal"; // slide-over version

export default function AppointmentsListPage() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({});
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState({
    services: [],
    resources: [],
    locations: [],
    reasons: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("list"); // "list" | "calendar"

  // slide-over state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create"); // "create" | "edit"
  const [drawerInitialData, setDrawerInitialData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const normalizeRows = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  };

  const load = async (params = {}) => {
    try {
      setLoading(true);
      setError("");
      const data = await getAppointments(params);
      setItems(normalizeRows(data));
    } catch (e) {
      console.error(e);
      setError(e.message || "No se pudo cargar las citas.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const data = await getAppointmentsConfig();
      setConfig({
        services: Array.isArray(data?.services) ? data.services : [],
        resources: Array.isArray(data?.resources) ? data.resources : [],
        locations: Array.isArray(data?.locations) ? data.locations : [],
        reasons: Array.isArray(data?.reasons) ? data.reasons : [],
      });
    } catch (e) {
      console.warn("No se pudo cargar config de citas", e);
    }
  };

  // Carga inicial
  useEffect(() => {
    load({});
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // filtros
  const handleFiltersChange = (next) => {
    setFilters(next || {});
  };
  const handleApplyFilters = () => load(filters || {});
  const handleClearFilters = () => {
    setFilters({});
    load({});
  };

  const stats = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const total = list.length;
    const byStatus = list.reduce((acc, a) => {
      const s = (a.status || "sin_estado").toLowerCase();
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
    return { total, byStatus };
  }, [items]);

  const calendarTimeZone = useMemo(() => {
    return (
      config?.settings?.timezone ||
      config?.locations?.find((l) => l.timezone)?.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "local"
    );
  }, [config]);

  // --- Drawer helpers ---
  // helper para normalizar valores de fecha a "YYYY-MM-DDTHH:mm"
  const toDateTimeLocalValue = (v) => {
    if (!v && v !== 0) return "";
    const formatDate = (d) => {
      if (!d || isNaN(d)) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    // Si viene como Date
    if (v instanceof Date && !isNaN(v)) {
      return formatDate(v);
    }
    // Si viene como número (timestamp ms)
    if (typeof v === "number") {
      const d = new Date(v);
      if (!isNaN(d)) return formatDate(d);
      return "";
    }
    // Si viene como string - intentamos crear Date si no tiene 'T'
    if (typeof v === "string") {
      // si ya es ISO con T y cumple longitud mínima, usamos la parte necesaria
      if (v.includes("T")) return v.replace(" ", "T").slice(0, 16);
      const d = new Date(v);
      if (!isNaN(d)) return formatDate(d);
      return "";
    }
    return "";
  };

  const openCreateDrawer = (initial = null) => {
    const normalized = initial
      ? {
          ...initial,
          // convertimos a start/end en formato compatible con <input datetime-local>
          start: toDateTimeLocalValue(initial.start ?? initial.start_at ?? initial.date ?? ""),
          end: toDateTimeLocalValue(initial.end ?? initial.end_at ?? ""),
        }
      : null;

    setDrawerInitialData(normalized);
    // Pequeño log para debug en caso de que siga fallando
    // console.log("openCreateDrawer -> normalized initial:", normalized);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const handleCreateFromCalendar = ({ start, end }) => {
    // Normalizamos y abrimos el drawer
    openCreateDrawer({ start, end, timezone: calendarTimeZone });
  };

  const openEditDrawer = async (appointment) => {
    setDrawerMode("edit");
    setDrawerInitialData(appointment || null);
    setDrawerOpen(true);
    if (!appointment?.id) return;
    try {
      const data = await getAppointment(appointment.id);
      setDrawerInitialData(data);
    } catch (e) {
      setError(e.message || "No se pudo cargar el detalle de la cita.");
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerInitialData(null);
  };


  // 👉 Movimiento de citas (drag & drop)
  const handleMoveFromCalendar = async ({ appointment, start, end, revert }) => {
    if (!appointment || !appointment.id) {
      if (revert) revert();
      return;
    }

    try {
      await updateAppointment(appointment.id, {
        start_at: start,
        end_at: end,
        actor_user_id: user?.id ?? null,
      });
      await load(filters || {});
    } catch (err) {
      console.error("Error al mover la cita", err);
      setError(err.message || "No se pudo mover la cita.");
      if (revert) revert();
    }
  };

  // Al hacer submit del formulario dentro del drawer
  const handleDrawerSubmit = async (formData) => {
    try {
      setSubmitting(true);
      setError("");
      const resourceId = formData.resource_id;
      const payload = {
        ...formData,
        resource_id: undefined,
        created_by_user_id: user?.id ?? null,
        actor_user_id: user?.id ?? null,
      };

      if (drawerMode === "create") {
        await createAppointment({
          ...payload,
          resources: resourceId ? [{ resource_id: resourceId, role: "principal" }] : [],
        });
      } else {
        const updated = await updateAppointment(drawerInitialData.id, payload);
        const currentResourceId = drawerInitialData?.resources?.[0]?.resource_id;
        if (resourceId && Number(resourceId) !== Number(currentResourceId || 0)) {
          if (currentResourceId) {
            await removeAppointmentResource(drawerInitialData.id, currentResourceId);
          }
          await addAppointmentResource(drawerInitialData.id, {
            resource_id: resourceId,
            role: "principal",
          });
        } else if (!resourceId && currentResourceId) {
          await removeAppointmentResource(drawerInitialData.id, currentResourceId);
        }
        setDrawerInitialData(updated);
      }

      // recargar lista para sincronizar
      await load(filters || {});

      // cerrar drawer
      closeDrawer();
    } catch (err) {
      console.error("Error creando/actualizando cita", err);
      setError(err.message || "Error al guardar la cita.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickStatus = async (appointment, status) => {
    if (!appointment?.id) return;
    try {
      setError("");
      const payload = {
        status,
        actor_user_id: user?.id ?? null,
      };
      if (status === "confirmed") {
        payload.requires_approval = false;
        payload.approved_at = new Date().toISOString();
        payload.approved_by_user_id = user?.id ?? null;
      }
      if (status === "canceled") {
        const reason = window.prompt("Motivo de cancelación", "");
        if (reason === null) return;
        payload.cancel_reason = reason;
        payload.canceled_at = new Date().toISOString();
        payload.canceled_by_user_id = user?.id ?? null;
      }
      await updateAppointment(appointment.id, payload);
      await load(filters || {});
    } catch (err) {
      setError(err.message || "No se pudo actualizar la cita.");
    }
  };

  const handleQuickPayment = async (appointment) => {
    if (!appointment?.id) return;
    const suggested = appointment.total_amount || appointment.service_price || "";
    const amountRaw = window.prompt("Monto recibido", suggested ? String(suggested) : "");
    if (amountRaw === null) return;
    const amount = Number(amountRaw);
    if (!amount || Number.isNaN(amount) || amount <= 0) {
      setError("Ingresa un monto válido para registrar el pago.");
      return;
    }
    const method = window.prompt("Método de pago", "efectivo");
    if (method === null) return;
    try {
      setError("");
      await addAppointmentPayment(appointment.id, {
        amount,
        method: method || "efectivo",
        captured_by_user_id: user?.id ?? null,
      });
      await load(filters || {});
    } catch (err) {
      setError(err.message || "No se pudo registrar el pago.");
    }
  };

  return (
    <div className="appointments-module p-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <div className="page-title">Citas</div>
          <p className="text-xs text-[color:var(--muted)]">
            Gestiona y visualiza las reservas en formato de lista o calendario.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Toggle Lista / Calendario */}
          <div className="inline-flex bg-[color:var(--surface-muted,#111827)] rounded-full p-1">
            <button
              type="button"
              className={
                "px-3 py-1 text-xs rounded-full " +
                (viewMode === "list"
                  ? "bg-[color:var(--accent,#3b82f6)] text-white"
                  : "text-[color:var(--muted)]")
              }
              onClick={() => setViewMode("list")}
            >
              Lista
            </button>
            <button
              type="button"
              className={
                "px-3 py-1 text-xs rounded-full " +
                (viewMode === "calendar"
                  ? "bg-[color:var(--accent,#3b82f6)] text-white"
                  : "text-[color:var(--muted)]")
              }
              onClick={() => setViewMode("calendar")}
            >
              Calendario
            </button>
          </div>

          <button
            onClick={() => openCreateDrawer()}
            className="btn"
            type="button"
          >
            Nueva cita
          </button>
        </div>
      </div>

      {/* Filtros */}
      <section className="card section">
        <div className="flex justify-between items-center gap-3 flex-wrap mb-3">
          <h3 className="m-0 text-sm font-semibold">Filtros</h3>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn btn-muted !px-2 !py-1 text-xs"
              onClick={handleClearFilters}
            >
              Limpiar
            </button>
            <button
              type="button"
              className="btn !px-2 !py-1 text-xs"
              onClick={handleApplyFilters}
            >
              Aplicar filtros
            </button>
          </div>
        </div>
        <AppointmentFilters onChange={handleFiltersChange} config={config} />
      </section>

      {/* Resumen rápido */}
      <section className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <div className="card p-3">
          <div className="text-[10px] text-[color:var(--muted)]">
            Total citas
          </div>
          <div className="text-lg font-semibold">{stats.total}</div>
        </div>

        {Object.entries(stats.byStatus).map(([status, count]) => (
          <div key={status} className="card p-3">
            <div className="text-[10px] uppercase text-[color:var(--muted)]">
              {status.replace("_", " ")}
            </div>
            <div className="text-base font-semibold">{count}</div>
          </div>
        ))}
      </section>

      {/* Contenido principal */}
      <section className="card section">
        <h3 className="m-0 mb-2 text-sm font-semibold">
          {viewMode === "list" ? "Listado de citas" : "Calendario de citas"}
        </h3>

        {error && (
          <div className="text-xs text-red-600 border border-red-300 rounded p-2 mb-2">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-xs text-[color:var(--muted)]">
            Cargando citas...
          </div>
        ) : viewMode === "list" ? (
          <AppointmentList
            items={Array.isArray(items) ? items : []}
            // en lugar de navegar, abrimos el drawer en modo edición
            onSelect={(a) => openEditDrawer(a)}
            onQuickStatus={handleQuickStatus}
            onQuickPayment={handleQuickPayment}
          />
        ) : (
          <AppointmentCalendar
            items={Array.isArray(items) ? items : []}
            onSelect={(a) => openEditDrawer(a)}
            onCreate={handleCreateFromCalendar}
            onMove={handleMoveFromCalendar}
            timeZone={calendarTimeZone}
          />
        )}
      </section>

      {/* Slide-over (drawer) */}
      <AppointmentFormModal
        open={drawerOpen}
        mode={drawerMode}
        initialData={drawerInitialData}
        config={config}
        onClose={closeDrawer}
        onSubmit={handleDrawerSubmit}
        submitting={submitting}
      />
    </div>
  );
}
